/**
 * Export scope - the time window a spreadsheet download covers.
 *
 * The download button on list pages (registrations, claims) lets the user pick
 * how much data to export: the current on-screen view, a preset period, or all
 * time. Presets and "All time" own the time dimension for the export - they
 * replace whatever time window the page's header TimeFilter has set, while
 * preserving the other active filters (search, status, attribute facets) so
 * "export this dealer's registrations for the last 90 days" works. "Current
 * view" exports exactly what is on screen.
 *
 * Every scope maps to the generic ``created_after`` / ``created_before`` query
 * params the list endpoints already honour (``apply_date_filter`` in
 * ``common/list_filters.py``); no backend change is needed.
 */

export type ExportScope =
  | 'current'
  | 'this_month'
  | 'last_30_days'
  | 'last_90_days'
  | 'this_year'
  | 'all_time'

export interface ExportScopeOption {
  value: ExportScope
  label: string
  /** Longer description shown under the label in the menu. */
  hint: string
}

export const EXPORT_SCOPE_OPTIONS: ExportScopeOption[] = [
  { value: 'current', label: 'Current view', hint: 'Everything matching your current filters' },
  { value: 'this_month', label: 'This month', hint: 'Registered since the 1st' },
  { value: 'last_30_days', label: 'Last 30 days', hint: 'Rolling 30-day window' },
  { value: 'last_90_days', label: 'Last 90 days', hint: 'Rolling 90-day window' },
  { value: 'this_year', label: 'This year', hint: `Since 1 Jan` },
  { value: 'all_time', label: 'All time', hint: 'Every record, ignoring the date filter' },
]

// Query params that carry a time window. A period/all scope strips these from
// the base params before applying its own window, so the page's header
// TimeFilter never leaks into a differently-scoped export.
const TIME_WINDOW_KEYS = ['created_after', 'created_before', 'date_preset']

function toYmd(d: Date): string {
  // Local-date ``YYYY-MM-DD`` - matches ``timeRangeToParams``
  // (lib/time-range-params) so an export window lines up with the on-screen filter.
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Resolve an export scope + the page's current params into the params sent with
 * the CSV download request.
 */
export function exportScopeToParams(
  scope: ExportScope,
  baseParams: Record<string, string>,
): Record<string, string> {
  // "Current view" is exactly what is on screen.
  if (scope === 'current') return { ...baseParams }

  // All other scopes own the time dimension: drop the page's time window, keep
  // the rest of the filters.
  const params: Record<string, string> = {}
  for (const [key, value] of Object.entries(baseParams)) {
    if (!TIME_WINDOW_KEYS.includes(key)) params[key] = value
  }

  const now = new Date()
  if (scope === 'this_month') {
    params.created_after = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  } else if (scope === 'last_30_days') {
    const start = new Date(now)
    start.setDate(now.getDate() - 30)
    params.created_after = toYmd(start)
  } else if (scope === 'last_90_days') {
    const start = new Date(now)
    start.setDate(now.getDate() - 90)
    params.created_after = toYmd(start)
  } else if (scope === 'this_year') {
    params.created_after = `${now.getFullYear()}-01-01`
  }
  // 'all_time' adds no window - every record.

  return params
}
